import { Question } from 'models/Question'
import { RootState } from 'redux/store'

// Visibility Rules Types
export const QUESTION_CONDITION_TYPES = [
  'yes_no_question',
  'onetime_donation_amount',
  'recurring_donation_amount',
] as const

export const YES_NO_CONDITION_VALUES = ['yes', 'no', ''] as const

export const OPERATOR_VALUES = ['=', '>=', '<='] as const

export type ConditionTypes = (typeof QUESTION_CONDITION_TYPES)[number]
export type YesNoConditionValue = (typeof YES_NO_CONDITION_VALUES)[number]
export type Operator = (typeof OPERATOR_VALUES)[number]

export interface QuestionCondition {
  type: ConditionTypes
  reference_id: string | null
  operator: Operator
  value: number | YesNoConditionValue
}

export interface QuestionVisibilityRule {
  logical_operator: 'and' | 'or'
  conditions: QuestionCondition[] | null
}

/**
 * Context object containing all the data needed to evaluate visibility rules
 */
export interface VisibilityEvaluationContext {
  // Current donation amount
  onetimeDonationAmount: number
  recurringDonationAmount: number

  // Current donation frequency to determine which amount to use
  donationFrequency: string

  // Answers to custom questions (for yes_no_question conditions)
  customAnswers: Record<string, string | number | boolean | undefined>

  // All questions (needed to find referenced questions)
  allQuestions: Question[]
}

/**
 * Creates a visibility evaluation context from Redux state
 */
export const createVisibilityContext = (
  state: RootState,
  allQuestions: Question[],
): VisibilityEvaluationContext => {
  const { amount, frequency, customAnswers } = state.order

  return {
    onetimeDonationAmount: frequency.value === 'one-time' ? amount.value : 0,
    recurringDonationAmount: frequency.value !== 'one-time' ? amount.value : 0,
    donationFrequency: frequency.value,
    customAnswers: customAnswers,
    allQuestions,
  }
}

/**
 * Evaluates a single condition against the current context
 */
export const evaluateCondition = (
  condition: QuestionCondition,
  context: VisibilityEvaluationContext,
): boolean => {
  const { type, operator, value, reference_id } = condition

  switch (type) {
    case 'onetime_donation_amount':
      if (context.donationFrequency !== 'one-time') {
        return false
      }

      return evaluateNumericCondition(context.onetimeDonationAmount, operator, value as number)

    case 'recurring_donation_amount':
      if (context.donationFrequency === 'one-time') {
        return false
      }

      return evaluateNumericCondition(context.recurringDonationAmount, operator, value as number)

    case 'yes_no_question':
      return evaluateYesNoQuestionCondition(reference_id, operator, value as string, context)

    default:
      console.warn(`Unknown condition type: ${type}`)
      return false
  }
}

/**
 * Evaluates numeric conditions (>=, <=, =)
 */
const evaluateNumericCondition = (
  currentValue: number,
  operator: Operator,
  targetValue: number,
): boolean => {
  switch (operator) {
    case '>=':
      return currentValue >= targetValue
    case '<=':
      return currentValue <= targetValue
    case '=':
      return currentValue === targetValue
    default:
      console.warn(`Unknown operator: ${operator}`)
      return false
  }
}

/**
 * Evaluates yes_no_question conditions by checking another question's answer
 */
const evaluateYesNoQuestionCondition = (
  referenceId: string | null,
  operator: Operator,
  targetValue: string,
  context: VisibilityEvaluationContext,
): boolean => {
  if (!referenceId) {
    console.warn('yes_no_question condition missing reference_id')
    return false
  }

  // Find the referenced question
  const referencedQuestion = context.allQuestions.find((q) => q.reference_id === referenceId)

  if (!referencedQuestion) {
    console.warn(`Referenced question not found: ${referenceId}`)
    return false
  }

  // Get the current answer for the referenced question
  const currentAnswer = context.customAnswers[referencedQuestion.id]

  // Handle empty/undefined answers
  if (currentAnswer === undefined || currentAnswer === null) {
    return targetValue === '' // Only match if target is also empty
  }

  // For yes_no questions, we typically only use '=' operator
  if (operator === '=') {
    return currentAnswer === targetValue
  }

  console.warn(`Unsupported operator for yes_no_question: ${operator}`)
  return false
}

/**
 * Evaluates a complete visibility rule (with logical operators)
 */
export const evaluateVisibilityRule = (
  rule: QuestionVisibilityRule,
  context: VisibilityEvaluationContext,
): boolean => {
  const { logical_operator, conditions } = rule

  // If no conditions, question is visible by default
  if (!conditions || conditions.length === 0) {
    return true
  }

  // Evaluate all conditions
  const conditionResults = conditions.map((condition) => evaluateCondition(condition, context))

  // Apply logical operator
  switch (logical_operator) {
    case 'and':
      return conditionResults.every((result) => result)
    case 'or':
      return conditionResults.some((result) => result)
    default:
      console.warn(`Unknown logical operator: ${logical_operator}`)
      return false
  }
}

/**
 * Main function to determine if a question should be visible
 */
export const isQuestionVisible = (
  question: Question,
  context: VisibilityEvaluationContext,
): boolean => {
  // If no visibility rule, question is visible by default
  if (!question.visibility_rule) {
    return true
  }

  return evaluateVisibilityRule(question.visibility_rule, context)
}

/**
 * Filters an array of questions based on their visibility rules
 */
export const filterVisibleQuestions = (
  questions: Question[],
  context: VisibilityEvaluationContext,
): Question[] => {
  return questions.filter((question) => isQuestionVisible(question, context))
}

/**
 * Hook-friendly function that takes Redux state and returns filtered questions
 */
export const getVisibleQuestions = (questions: Question[], state: RootState): Question[] => {
  const context = createVisibilityContext(state, questions)

  return filterVisibleQuestions(questions, context)
}
